/**
 * Client desk theme preference — account-persisted (Starter+).
 */

import type { ThemeMode } from '@/shared/types/theme.types'
import type { DeskTokenSet } from '@/shared/theme/systems/desk-tokens'
import type { PresetThemeId } from '@/shared/theme/catalog'

export type ThemeSystemRef =
  | { kind: 'preset'; id: PresetThemeId }
  | { kind: 'custom'; id: string }

export interface CustomDeskSystem {
  id: string
  name: string
  tokens: DeskTokenSet
  /** Tokens apply for this mode; opposite mode uses desk pair fallback */
  preferredMode: 'light' | 'dark'
  basedOn?: PresetThemeId
  createdAt: string
  updatedAt: string
}

export interface ClientThemePreference {
  userId: string
  mode: ThemeMode
  active: ThemeSystemRef
  customs: CustomDeskSystem[]
  updatedAt: string
}

const PRESET_IDS = new Set([
  'mechagora',
  'midnight-slate',
  'cream-paper',
  'accessible-signals',
])

export function parseSystemRef(raw: string): ThemeSystemRef | null {
  if (raw.startsWith('preset:')) {
    const id = raw.slice('preset:'.length)
    if (PRESET_IDS.has(id)) {
      return { kind: 'preset', id: id as PresetThemeId }
    }
    return null
  }
  if (raw.startsWith('custom:')) {
    return { kind: 'custom', id: raw.slice('custom:'.length) }
  }
  if (PRESET_IDS.has(raw)) {
    return { kind: 'preset', id: raw as PresetThemeId }
  }
  return null
}

export function systemRefKey(ref: ThemeSystemRef): string {
  return ref.kind === 'preset' ? `preset:${ref.id}` : `custom:${ref.id}`
}
